/**
 * Registering the offline worker (#43) — and failing QUIETLY. The page's CSP
 * carries `worker-src blob:` until the nginx side allows `'self'` (an owner's
 * decision for the shared vhost); until then the page must work exactly as it did
 * before. So the page does not even try while its own policy — the `<meta>` CSP
 * stamped from `src/lib/csp.ts`, which moves only together with nginx — refuses a
 * same-origin worker: no attempt, no CSP violation line in the console. A refusal
 * that still happens (a header stricter than the meta) is swallowed.
 */

type Registration = { active?: { scriptURL: string } | null; installing?: { scriptURL: string } | null; waiting?: { scriptURL: string } | null; unregister(): Promise<boolean> };
type Registrar = { register(url: string): Promise<unknown>; getRegistrations?(): Promise<readonly Registration[]> };
type Caches = { keys(): Promise<string[]>; delete(name: string): Promise<boolean> };

/**
 * The way back (Codex review): a page that may not run the worker — its CSP refuses it,
 * or the build says `VITE_PH_OFFLINE_SW=off` — also removes one installed earlier, with
 * its caches (thread states included). Best effort, never an error on the page.
 */
export async function retireOfflineWorker(container: Registrar, storage: Caches | undefined): Promise<void> {
  try {
    const registrations = (await container.getRegistrations?.()) ?? [];
    await Promise.all(
      registrations
        .filter((r) => [r.active, r.waiting, r.installing].some((w) => w?.scriptURL.endsWith('/sw.js')))
        .map((r) => r.unregister()),
    );
    if (storage) {
      const names = await storage.keys();
      await Promise.all(names.filter((name) => name.startsWith('ph-')).map((name) => storage.delete(name)));
    }
  } catch {
    // nothing to retire, or a browser that will not say
  }
}

/** Whether a policy lets the page start a worker from its own origin. */
export function allowsOwnWorker(policy: string): boolean {
  const directive = policy
    .split(';')
    .map((part) => part.trim().split(/\s+/))
    .find(([name]) => name === 'worker-src');
  return !directive || directive.slice(1).includes("'self'");
}

export async function registerOfflineWorker(
  container: Registrar | undefined,
  production: boolean,
  policy: string,
  enabled = true,
  storage?: Caches,
): Promise<boolean> {
  if (!production || !container) return false;
  if (!enabled || !allowsOwnWorker(policy)) {
    await retireOfflineWorker(container, storage);
    return false;
  }
  try {
    await container.register('/sw.js');
    return true;
  } catch {
    return false;
  }
}
